import {token} from "@atlaskit/tokens";
import {useNavigate} from "@tanstack/react-router";
import type {CSSProperties, ReactNode} from "react";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {twJoin} from "tailwind-merge";
import {EventEditPanel} from "~/components/eventComponents/eventEditPanel";
import {EventViewPanel} from "~/components/eventComponents/eventViewPanel";
import type {EventsTabProfileListOverrides} from "~/components/entityProfile/eventsTabProfileListOverrides";
import {ENTITY_PROFILE_HORIZONTAL_INSET_CLASS} from "~/components/entityProfile/entityProfileChromeClasses";
import {useJourneyProfileEventsListPresentation} from "~/components/entityProfile/useJourneyProfileEventsListPresentation";
import {useDataViewerViewModeControl} from "~/components/entityProfile/useDataViewerViewModeControl";
import {createOttopilotListView} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useEventRowEngagement} from "~/components/ottopilotDataViewer/rowEngagement/useEventRowEngagement";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {useListNotesPanel} from "~/components/notes/useListNotesPanel";
import {ViewModeSegmentedControl} from "~/components/viewModeSegmentedControl";
import {resolveEventContainerEditPanelGlobalConstraints} from "~/utilities/eventContainerGlobalConstraints";
import type {
    TableRowActionsConfig,
    TableRowActionsToolbarItem,
} from "~/components/table/tableRowActions.types";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {usePlatformUserDetails, useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {JOURNEY_EVENTS_MODULE_DATA_VIEWER_QUERY_KEY} from "~/utilities/archiveEventContainerWithEvents";
import {invalidateConfigDrivenWorkspaceQueries} from "~/workspaceConfig/invalidateConfigDrivenWorkspaceQueries";
import {
    BobcardEventTypes,
    BOBCARD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    BOBCARD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    BOBCARD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    BOBCARD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    BOBCARD__EVENT__CALLS__STATUS__COLUMN_ID,
    BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    bobcardCallStatuses,
    isBobcardWorkspace,
    bobcardEnquiryDigitalLeadStatuses,
} from "~/domainModels/workspaceSpecific/bobcard";
import {
    DHIRAAGU__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID,
    DHIRAAGU__EVENT__SALES__STATUS__COLUMN_ID,
    DHIRAAGU__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    callStatuses as dhiraaguCallStatuses,
    DhiraaguEventTypes,
    lastEventTypes as dhiraaguLastEventTypes,
    salesStatuses as dhiraaguSalesStatuses,
    isDhiraaguWorkspace,
} from "~/domainModels/workspaceSpecific/dhiraagu";
import {
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AmaronEventTypes,
    amaronCallStatuses,
    amaronEnquiryStatuses,
    amaronServiceCategories,
    getAmaronEventsPageScopeFilterConfig,
    isAmaronWorkspace,
    resolveAmaronCallScheduledOnDisplayValue,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_OWNER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EmotoradEventTypes,
    emotoradCallStatuses,
    emotoradEnquiryDigitalLeadStatuses,
    emotoradLeadEventStatuses,
    emotoradSalesStatuses,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DuluxEventTypes,
    callStatuses,
    enquiryStatuses,
    onboardingStatuses,
} from "~/domainModels/workspaceSpecific/dulux";
import {EventKanbanBoardCard} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-event-kanban-board";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getEvents_Length, getEventsWithEventContainers, type EventWithEventContainer, type EventContainerWithEvents} from "~/utilities/apis/api";
import {formatDateTime, getUserDisplayName} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";

const EVENT_HISTORY_FRONTEND_ONLY_SORT_COLUMN_IDS = new Set([
    "eventName",
    "eventStatus",
    "eventOwner",
    "scheduledOn",
    "loggedOn",
    "createdAtTimestamp",
    "journeyOwner",
]);

function sanitizeEventHistorySortConfiguration(sortConfiguration: SortConfiguration): SortConfiguration {
    return {
        ...sortConfiguration,
        lineItems: sortConfiguration.lineItems.filter(
            (lineItem) => EVENT_HISTORY_FRONTEND_ONLY_SORT_COLUMN_IDS.has(lineItem.columnId) !== true,
        ),
    };
}

/**
 * Kanban columns filter by `templateId`, but the journey-events API path does not
 * reliably apply that field as a Sriman filter. Strip it for the network call and
 * apply client-side after fetch (journey profile has a small event set).
 */
function extractClientSideTemplateIdFilter(filterConfiguration: FilterConfiguration): {
    apiFilterConfiguration: FilterConfiguration;
    templateIds: Array<string> | null;
} {
    const templateIds = new Set<string>();
    const remainingLineItems = filterConfiguration.lineItems.filter((lineItem) => {
        if (lineItem.columnId !== "templateId") {
            return true;
        }
        if (lineItem.operation === FilterOperation.in) {
            for (const value of lineItem.values) {
                templateIds.add(String(value));
            }
            return false;
        }
        if (lineItem.operation === FilterOperation.equals) {
            templateIds.add(String(lineItem.value));
            return false;
        }
        return true;
    });

    return {
        apiFilterConfiguration: {
            revisionId: filterConfiguration.revisionId,
            lineItems: remainingLineItems,
        },
        templateIds: templateIds.size > 0 ? Array.from(templateIds) : null,
    };
}

function applyClientSideTemplateIdFilter<T extends {templateId: Uuid}>(
    rows: Array<T>,
    templateIds: Array<string> | null,
): Array<T> {
    if (templateIds === null) {
        return rows;
    }
    const allowed = new Set(templateIds);
    return rows.filter((row) => allowed.has(String(row.templateId)));
}

const JOURNEY_EVENTS_TABLE_VIEW_ID = uniqueUuid`61b98ad5-9f16-4e90-a09a-105b6af5f783`;
const JOURNEY_EVENTS_KANBAN_VIEW_ID = uniqueUuid`2cab1411-4fd4-4e09-91b7-c61837ded0ab`;
const JOURNEY_PROFILE_EVENTS_LIST_VIEW_ID = uniqueUuid`a3f2e1d0-4c5b-6a7f-8e9d-0c1b2a3f4e5d`;
const JOURNEY_EVENTS_QUERY_KEY_PREFIX_ID = JOURNEY_EVENTS_MODULE_DATA_VIEWER_QUERY_KEY;
const DEFAULT_PROFILE_EVENTS_ROW_TOOLBAR = ["edit", "notes", "archive"] as const satisfies ReadonlyArray<TableRowActionsToolbarItem>;

export type {EventsTabProfileListOverrides};

export function EventsTab({
    journey,
    className,
    style,
    presentation = "legacy",
    dismissFooterEngagementPanels,
    registerDismissEventRowEngagement,
    profileFilterLeadingContent,
    profileListOverrides,
}: {
    journey: EventContainerWithEvents;
    className?: string;
    style?: CSSProperties;
    presentation?: "legacy" | "profile";
    dismissFooterEngagementPanels?: () => void;
    registerDismissEventRowEngagement?: (dismiss: () => void) => void;
    /** When set (harness JourneyProfile), drives views / All / Display by / sort / row CTAs. */
    profileListOverrides?: EventsTabProfileListOverrides;
    /** Injected before Event Type in the profile filter row (e.g. customer Lifecycle scope). */
    profileFilterLeadingContent?: ReactNode;
}) {
    const navigate = useNavigate();
    const isProfilePresentation = presentation === "profile";
    const profileEnabledViewTypes = profileListOverrides?.enabledViewTypes;
    const showProfileListView =
        isProfilePresentation === true &&
        (profileEnabledViewTypes === undefined || profileEnabledViewTypes.includes("list") === true);
    const showTableView =
        isProfilePresentation !== true ||
        profileEnabledViewTypes === undefined ||
        profileEnabledViewTypes.includes("table") === true;
    const showKanbanView =
        isProfilePresentation !== true ||
        profileEnabledViewTypes === undefined ||
        profileEnabledViewTypes.includes("kanban") === true;
    const profileDefaultViewType =
        profileListOverrides?.defaultViewType ?? (isProfilePresentation === true ? "list" : "table");
    const profileRowToolbar = profileListOverrides?.rowToolbar ?? DEFAULT_PROFILE_EVENTS_ROW_TOOLBAR;
    const eventsDataViewerControllerRef = useRef<OttopilotDataViewerController<EventWithEventContainer> | null>(null);
    const eventsQueryKeyPrefix = useMemo(() => [JOURNEY_EVENTS_QUERY_KEY_PREFIX_ID, journey.id], [journey.id]);
    const colorsData = [
        {
            textColor: token("color.text.accent.red"),
            background: token("color.background.accent.red.subtlest"),
        },
        {
            textColor: token("color.text.accent.blue"),
            background: token("color.background.accent.blue.subtlest"),
        },
        {
            textColor: token("color.text.accent.green"),
            background: token("color.background.accent.green.subtlest"),
        },
        {
            textColor: token("color.text.accent.orange"),
            background: token("color.background.accent.orange.subtlest"),
        },
    ];
    const {heimdallUser} = useWorkspaceUser();
    const queryClient = useQueryClient();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(journey.templateId);
    const profileLeadOwnerColumnId = eventContainerTemplate.configuration.eventContainersPage.leadOwnerColumnId;
    const amaronAssignedAgentColumnId =
        eventContainerTemplate.configuration.eventContainersPage.leadOwnerColumnId ??
        AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID;

    // const eventKanbanRowActionsConfig = useMemo((): TableRowActionsConfig<EventWithEventContainer> => {
    //     return {
    //         toolbar: ["view"],
    //         moreMenu: [],
    //         onView: (event) => {
    //             void navigate({
    //                 to: "/w/$workspaceId/ottopilot/event-containers/$eventContainerId/events/$eventId",
    //                 params: {
    //                     workspaceId: workspace.id,
    //                     eventContainerId: journey.id,
    //                     eventId: event.id,
    //                 },
    //             });
    //         },
    //     };
    // }, [journey.id, navigate, workspace.id]);

    const users = useUsers();
    const userTemplates = useUserTemplates();
    const platformUserDetails = usePlatformUserDetails();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const resolveEmotoradStatusDisplayName = (statusId: unknown, statuses: ReadonlyArray<{id: Uuid; displayName: string}>): string => {
        if (statusId === null || statusId === undefined) {
            return "-";
        }
        const status = statuses.find((entry) => String(entry.id) === String(statusId));
        return status?.displayName ?? "-";
    };

    const resolveEmotoradCallStyleEventOwnerId = (event: EventWithEventContainer): Uuid | null => {
        const loggedBy = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID] as Uuid | null;
        if (loggedBy !== null && loggedBy !== undefined) {
            return loggedBy;
        }
        return event.additionalColumnValues[EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID] as Uuid | null;
    };

    const resolveEmotoradWalkinStyleEventOwnerId = (event: EventWithEventContainer): Uuid | null => {
        const loggedBy = event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID] as Uuid | null;
        if (loggedBy !== null && loggedBy !== undefined) {
            return loggedBy;
        }
        return event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID] as Uuid | null;
    };

    const resolveEmotoradTestRideStyleEventOwnerId = (event: EventWithEventContainer): Uuid | null => {
        const loggedBy = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID] as Uuid | null;
        if (loggedBy !== null && loggedBy !== undefined) {
            return loggedBy;
        }
        return event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID] as Uuid | null;
    };

    const resolveUserDisplayName = ({userId}: {userId: Uuid | null | undefined}): string => {
        if (userId === null || userId === undefined) {
            return "-";
        }
        // Names live on platform user details; workspace user templates no longer carry identity columns.
        return getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId);
    };

    const resolveAmaronAssignedAgentUserId = (event: EventWithEventContainer): Uuid | null => {
        const fromEventJourney = event.eventContainer.additionalColumnValues[amaronAssignedAgentColumnId];
        if (typeof fromEventJourney === "string" && fromEventJourney.trim() !== "") {
            return fromEventJourney as Uuid;
        }
        const fromProfileJourney = journey.additionalColumnValues[amaronAssignedAgentColumnId];
        if (typeof fromProfileJourney === "string" && fromProfileJourney.trim() !== "") {
            return fromProfileJourney as Uuid;
        }
        return null;
    };

    const journeyMutationGlobalConstraints = useMemo(
        () =>
            resolveEventContainerEditPanelGlobalConstraints({
                workspace,
                workspaceCapabilities,
                editPanel: eventContainerTemplate.configuration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, eventContainerTemplate.configuration.eventContainersPage.editPanel],
    );

    const {openNotesPanel, closeNotesPanel, listNotesPanelChrome, notesPanelContext} = useListNotesPanel();

    const eventRowEngagement = useEventRowEngagement({
        workspaceId: workspace.id,
        navigate,
        isArchivedView: false,
        globalConstraints: isProfilePresentation ? journeyMutationGlobalConstraints : undefined,
        listModuleConfig: null,
        showDisposition: false,
        showNotes: isProfilePresentation,
        includeCreateDrawer: false,
        listCreateDrawerItemsCount: 0,
        queryKeyPrefix: eventsQueryKeyPrefix,
        controllerRef: eventsDataViewerControllerRef,
        dispositionEventId: null,
        notesPanelEventContainerId: isProfilePresentation ? (notesPanelContext?.eventContainerId ?? null) : null,
        openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel: eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase,
        isCreateDrawerOpen: false,
        onOpenDisposition: () => undefined,
        onCloseDisposition: () => undefined,
        onCreateDrawerOpenChange: () => undefined,
        onScheduleSuccess: () => {
            void invalidateConfigDrivenWorkspaceQueries({
                queryClient,
                workspaceId: workspace.id,
            });
        },
        afterArchiveInvalidate: async () => {
            await invalidateConfigDrivenWorkspaceQueries({
                queryClient,
                workspaceId: workspace.id,
            });
        },
    });

    const profileTableRowActions = useMemo(() => {
        if (isProfilePresentation !== true) {
            return undefined;
        }

        const base = eventRowEngagement.tableRowActions;

        const wrapRowHandler = <T extends EventWithEventContainer>(
            handler: ((row: T) => void) | undefined,
        ): ((row: T) => void) | undefined => {
            if (handler === undefined) {
                return undefined;
            }
            return (row: T) => {
                dismissFooterEngagementPanels?.();
                handler(row);
            };
        };

        const editEnabledEventTemplateIds = new Set(
            profileListOverrides?.editEnabledEventTemplateIds ?? [],
        );
        const editDisabledEventTemplateIds = new Set(
            profileListOverrides?.editDisabledEventTemplateIds ?? [],
        );
        const isEventEditHidden = (row: EventWithEventContainer) => {
            if (editEnabledEventTemplateIds.size > 0) {
                return editEnabledEventTemplateIds.has(row.templateId) !== true;
            }
            return editDisabledEventTemplateIds.has(row.templateId) === true;
        };
        const isRowActionDisabledByConfig = (
            item: TableRowActionsToolbarItem,
            row: EventWithEventContainer,
        ) => {
            return profileListOverrides?.isRowActionDisabled?.(item, row) === true;
        };

        return {
            ...base,
            toolbar: [...profileRowToolbar],
            moreMenu:
                profileListOverrides?.moreMenu === undefined ? undefined : [...profileListOverrides.moreMenu],
            onProfile: undefined,
            onEdit: wrapRowHandler((row) => {
                if (isEventEditHidden(row) === true || isRowActionDisabledByConfig("edit", row) === true) {
                    return;
                }
                base.onEdit?.(row);
            }),
            onNotes: wrapRowHandler(base.onNotes),
            onArchive: wrapRowHandler(base.onArchive),
            canEdit: (row) => {
                if (isEventEditHidden(row) === true || isRowActionDisabledByConfig("edit", row) === true) {
                    return false;
                }
                return base.canEdit?.(row) ?? true;
            },
            isToolbarItemVisible: (item, row) => {
                if (item === "edit" && isEventEditHidden(row) === true) {
                    return false;
                }
                return base.isToolbarItemVisible?.(item, row) ?? true;
            },
            isToolbarItemDisabled: (item, row) => {
                if (item === "edit" && isEventEditHidden(row) === true) {
                    return true;
                }
                if (isRowActionDisabledByConfig(item, row) === true) {
                    return true;
                }
                return base.isToolbarItemDisabled?.(item, row) === true;
            },
            isMenuItemDisabled: (item, row) => {
                if (item === "edit" && isEventEditHidden(row) === true) {
                    return true;
                }
                if (profileListOverrides?.isRowActionDisabled?.(item, row) === true) {
                    return true;
                }
                return base.isMenuItemDisabled?.(item, row) === true;
            },
            useBrandToolbarIconColors: true,
        } satisfies TableRowActionsConfig<EventWithEventContainer>;
    }, [
        dismissFooterEngagementPanels,
        eventRowEngagement.tableRowActions,
        isProfilePresentation,
        profileListOverrides,
        profileRowToolbar,
    ]);

    const profileEventsListPresentation = useJourneyProfileEventsListPresentation({
        eventTemplates,
        workspaceCapabilities,
        tableRowActionsConfig: profileTableRowActions,
        resolveCreatedByDisplayName: (userId) => resolveUserDisplayName({userId}),
    });

    const profileEventTypeFilterColumn = useMemo(
        () => ({
            id: "templateId",
            displayName: "Event Type",
            type: OttopilotColumnType.uuid,
            sortingCapability: false,
            filteringCapability_contains: false,
            filteringCapability_in: {},
            valueGetter: (event: EventWithEventContainer) => event.templateId,
            valueFormatter: (value: unknown) =>
                eventTemplates.find((eventTemplate) => eventTemplate.id === value)?.displayName ?? "?",
        }),
        [eventTemplates],
    );

    const profileScopeFilterConfig = useMemo(() => {
        if (isProfilePresentation !== true) {
            return undefined;
        }
        if (profileListOverrides?.scopeFilterConfig !== undefined) {
            return profileListOverrides.scopeFilterConfig;
        }
        if (isAmaronWorkspace(workspaceCapabilities) !== true || profileLeadOwnerColumnId === undefined) {
            return undefined;
        }
        return getAmaronEventsPageScopeFilterConfig(profileLeadOwnerColumnId);
    }, [
        isProfilePresentation,
        profileLeadOwnerColumnId,
        profileListOverrides?.scopeFilterConfig,
        workspaceCapabilities,
    ]);

    const openProfileListCardReadPanel = useCallback((event: EventWithEventContainer) => {
        eventsDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel({
            rowEngagementType: RowEngagementType.read,
            rowEngagementPanelType: RowEngagementPanelType.sidebar,
            rowEngagementData: event,
        });
    }, []);

    const notesEventContainerSourceLabel =
        eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase;

    // Table column configuration for events
    const eventsDataViewerController = useOttopilotDataViewerController<EventWithEventContainer>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Event",
            pluralSentenceCase: "Events",
            singularLowerCase: "event",
            pluralLowerCase: "events",
        },
        queryKeyPrefix: eventsQueryKeyPrefix,
        rowsQueryFn: async ({filterConfiguration, limit, offset, searchConfiguration, sortConfiguration}) => {
            const {apiFilterConfiguration, templateIds} = extractClientSideTemplateIdFilter(filterConfiguration);
            // When kanban (or Event Type) filters by templateId, fetch a wider page then
            // slice client-side so column buckets are not empty from unsupported API filters.
            const shouldClientFilterByTemplate = templateIds !== null;
            const fetchLimit = shouldClientFilterByTemplate === true ? Math.max(limit + offset, 200) : limit;
            const fetchOffset = shouldClientFilterByTemplate === true ? 0 : offset;

            const eventsResult = await getEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    revisionId: apiFilterConfiguration.revisionId,
                    lineItems: [
                        // TODO: Ensure these lineItems are not on eventContainerId?
                        ...apiFilterConfiguration.lineItems,
                        {
                            columnId: "eventContainerId",
                            operation: FilterOperation.equals,
                            value: journey.id,
                        },
                    ],
                },
                sortConfiguration: sanitizeEventHistorySortConfiguration(sortConfiguration),
                searchConfiguration: searchConfiguration,
                limit: fetchLimit,
                offset: fetchOffset,
                workspaceId: workspace.id,
            });
            if (eventsResult.success !== true) {
                throw eventsResult.error;
            }
            // Profile Event Container is the source of truth for assigned agent and container fields.
            // Nested `event.eventContainer` from the events API can omit additional columns.
            const eventContainerForEventRows = {
                id: journey.id,
                createdBy: journey.createdBy,
                createdAt: journey.createdAt,
                updatedBy: journey.updatedBy,
                updatedAt: journey.updatedAt,
                status: journey.status,
                additionalColumnValues: journey.additionalColumnValues,
                revisionId: journey.revisionId,
                templateId: journey.templateId,
            };
            const mapped = eventsResult.data.map((event) => ({
                ...event,
                eventContainer: eventContainerForEventRows,
            }));
            const filtered = applyClientSideTemplateIdFilter(mapped, templateIds);
            if (shouldClientFilterByTemplate === true) {
                return filtered.slice(offset, offset + limit);
            }
            return filtered;
        },
        columnValuesQueryFn: async ({columnId, filterConfiguration, searchConfiguration}) => {
            if (isProfilePresentation !== true || columnId !== "templateId") {
                return [];
            }

            const {apiFilterConfiguration} = extractClientSideTemplateIdFilter(filterConfiguration ?? {
                revisionId: uniqueUuid`f1e2d3c4-b5a6-7890-abcd-ef1234567890`,
                lineItems: [],
            });

            const eventsResult = await getEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    revisionId: apiFilterConfiguration.revisionId,
                    lineItems: [
                        ...apiFilterConfiguration.lineItems,
                        {
                            columnId: "eventContainerId",
                            operation: FilterOperation.equals,
                            value: journey.id,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: uniqueUuid`e8f7d6c5-b4a3-2910-8f7e-6d5c4b3a2910`,
                    lineItems: [],
                },
                searchConfiguration: searchConfiguration,
                limit: 1000,
                offset: 0,
                workspaceId: workspace.id,
            });
            if (eventsResult.success !== true) {
                throw eventsResult.error;
            }

            const templateIdSet = new Set<string>();
            for (const event of eventsResult.data) {
                templateIdSet.add(String(event.templateId));
            }
            return Array.from(templateIdSet).sort();
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const {apiFilterConfiguration, templateIds} = extractClientSideTemplateIdFilter(filterConfiguration);
            if (templateIds !== null) {
                const eventsResult = await getEventsWithEventContainers({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    filterConfiguration: {
                        revisionId: apiFilterConfiguration.revisionId,
                        lineItems: [
                            ...apiFilterConfiguration.lineItems,
                            {
                                columnId: "eventContainerId",
                                operation: FilterOperation.equals,
                                value: journey.id,
                            },
                        ],
                    },
                    sortConfiguration: {
                        revisionId: uniqueUuid`e8f7d6c5-b4a3-2910-8f7e-6d5c4b3a2910`,
                        lineItems: [],
                    },
                    searchConfiguration: searchConfiguration,
                    limit: 1000,
                    offset: 0,
                    workspaceId: workspace.id,
                });
                if (eventsResult.success !== true) {
                    throw eventsResult.error;
                }
                return applyClientSideTemplateIdFilter(eventsResult.data, templateIds).length;
            }

            const eventsLengthResult = await getEvents_Length({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    revisionId: apiFilterConfiguration.revisionId,
                    lineItems: [
                        // TODO: Ensure these lineItems are not on eventContainerId?
                        ...apiFilterConfiguration.lineItems,
                        {
                            columnId: "eventContainerId",
                            operation: FilterOperation.equals,
                            value: journey.id,
                        },
                    ],
                },
                searchConfiguration: searchConfiguration,
                workspaceId: workspace.id,
            });
            if (eventsLengthResult.success !== true) {
                throw eventsLengthResult.error;
            }
            const eventsLength = eventsLengthResult.data;

            return eventsLength;
        },
        columns: {
            revisionId: uniqueUuid`d4c2aa24-324c-4d26-b625-e32018d53b31`,
            lineItems: (() => {
                const baseLineItems = (() => {
                if (isDhiraaguWorkspace(workspace)) {
                    return [
                        {
                            id: "eventName",
                            displayName: "Event Name",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const entry = Object.entries(dhiraaguLastEventTypes).find(([, v]) => v.id === event.templateId);
                                return entry ? entry[1].displayName : "Unknown";
                            },
                        },
                        {
                            id: "eventStatus",
                            displayName: "Status",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (DhiraaguEventTypes.call as Uuid)) {
                                    const statusId = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID];
                                    const callStatus = statusId ? Object.values(dhiraaguCallStatuses).find((s) => s.id === statusId) : undefined;
                                    return callStatus?.displayName ?? "-";
                                }
                                if (event.templateId === (DhiraaguEventTypes.sale as Uuid)) {
                                    const statusId = event.additionalColumnValues[DHIRAAGU__EVENT__SALES__STATUS__COLUMN_ID];
                                    const saleStatus = statusId ? Object.values(dhiraaguSalesStatuses).find((s) => s.id === statusId) : undefined;
                                    return saleStatus?.displayName ?? "-";
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdBy",
                            displayName: "Created By",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                return resolveUserDisplayName({
                                    userId: event.createdBy,
                                });
                            },
                        },
                        {
                            id: "createdAt",
                            displayName: "Created/Assigned On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "eventOwner",
                            displayName: "Event Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                let ownerId: Uuid | null = null;
                                if (event.templateId === (DhiraaguEventTypes.call as Uuid)) {
                                    ownerId = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__LOGGED_BY__COLUMN_ID] as Uuid | null;
                                    if (ownerId === null || ownerId === undefined) {
                                        ownerId = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID] as Uuid | null;
                                    }
                                }
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                        {
                            id: "scheduledOn",
                            displayName: "Scheduled On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (DhiraaguEventTypes.call as Uuid)) {
                                    const scheduleDate = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID];
                                    if (scheduleDate) {
                                        return formatDateTime(scheduleDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "loggedOn",
                            displayName: "Logged On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (DhiraaguEventTypes.call as Uuid)) {
                                    const loggedDate = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__LOGGED_ON__COLUMN_ID];
                                    if (loggedDate) {
                                        return formatDateTime(loggedDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdAtTimestamp",
                            displayName: "Created at",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "journeyOwner",
                            displayName: "Lead Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const ownerId = event.eventContainer.additionalColumnValues[DHIRAAGU__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID] as Uuid | null;
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                    ];
                }

                if (isBobcardWorkspace(workspace)) {
                    return [
                        {
                            id: "eventName",
                            displayName: "Event Name",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const eventTemplate = eventTemplates.find((t) => t.id === event.templateId);
                                return eventTemplate !== undefined ? eventTemplate.displayName : "Unknown";
                            },
                        },
                        {
                            id: "eventStatus",
                            displayName: "Status",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (BobcardEventTypes.call as Uuid)) {
                                    const statusId = event.additionalColumnValues[BOBCARD__EVENT__CALLS__STATUS__COLUMN_ID];
                                    const callStatus = statusId ? Object.values(bobcardCallStatuses).find((s) => s.id === statusId) : undefined;
                                    return callStatus?.displayName ?? "-";
                                }
                                if (event.templateId === (BobcardEventTypes.applicationsInitiated as Uuid)) {
                                    const statusId = event.additionalColumnValues[BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];

                                    const status = statusId ? Object.values(bobcardEnquiryDigitalLeadStatuses).find((s) => s.id === statusId) : undefined;
                                    return status?.displayName ?? "-";
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdBy",
                            displayName: "Created By",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                return resolveUserDisplayName({
                                    userId: event.createdBy,
                                });
                            },
                        },
                        {
                            id: "createdAt",
                            displayName: "Created/Assigned On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "eventOwner",
                            displayName: "Event Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                let ownerId: Uuid | null = null;
                                if (event.templateId === (BobcardEventTypes.call as Uuid)) {
                                    ownerId = event.additionalColumnValues[BOBCARD__EVENT__CALLS__LOGGED_BY__COLUMN_ID] as Uuid | null;
                                    if (ownerId === null || ownerId === undefined) {
                                        ownerId = event.additionalColumnValues[BOBCARD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID] as Uuid | null;
                                    }
                                }
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                        {
                            id: "scheduledOn",
                            displayName: "Scheduled On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (BobcardEventTypes.call as Uuid)) {
                                    const scheduleDate = event.additionalColumnValues[BOBCARD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID];
                                    if (scheduleDate) {
                                        return formatDateTime(scheduleDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "loggedOn",
                            displayName: "Logged On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (BobcardEventTypes.call as Uuid)) {
                                    const loggedDate = event.additionalColumnValues[BOBCARD__EVENT__CALLS__LOGGED_ON__COLUMN_ID];
                                    if (loggedDate) {
                                        return formatDateTime(loggedDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdAtTimestamp",
                            displayName: "Created at",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "journeyOwner",
                            displayName: "Lead Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const ownerId = event.eventContainer.additionalColumnValues[BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID] as Uuid | null;
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                    ];
                }

                if (isAmaronWorkspace(workspaceCapabilities)) {
                    return [
                        {
                            id: "eventName",
                            displayName: "Event Name",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const eventTemplate = eventTemplates.find((t) => t.id === event.templateId);
                                return eventTemplate?.displayName ?? "Unknown";
                            },
                        },
                        {
                            id: "eventStatus",
                            displayName: "Status",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (AmaronEventTypes.Enquiry as Uuid)) {
                                    const statusId = event.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
                                    const status = statusId ? Object.values(amaronEnquiryStatuses).find((s) => s.id === statusId) : undefined;
                                    return status?.displayName ?? "-";
                                }
                                if (event.templateId === (AmaronEventTypes.Calls as Uuid)) {
                                    const statusId = event.additionalColumnValues[AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
                                    const status = statusId ? Object.values(amaronCallStatuses).find((s) => s.id === statusId) : undefined;
                                    return status?.displayName ?? "-";
                                }
                                if (event.templateId === (AmaronEventTypes.Service as Uuid)) {
                                    const statusId = event.additionalColumnValues[AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID];
                                    const status = statusId ? Object.values(amaronServiceCategories).find((s) => s.id === statusId) : undefined;
                                    return status?.displayName ?? "-";
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdBy",
                            displayName: "Created By",
                            type: OttopilotColumnType.string,
                            sortingCapability: true,
                            valueGetter: (event: EventWithEventContainer) =>
                                resolveUserDisplayName({
                                    userId: event.createdBy,
                                }),
                        },
                        {
                            id: "createdAt",
                            displayName: "Created/Assigned On",
                            type: OttopilotColumnType.string,
                            sortingCapability: true,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "eventOwner",
                            displayName: "Event Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                // Event Owner mirrors journey Assigned Agent.
                                return resolveUserDisplayName({
                                    userId: resolveAmaronAssignedAgentUserId(event),
                                });
                            },
                        },
                        {
                            id: "scheduledOn",
                            displayName: "Scheduled On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (AmaronEventTypes.Calls as Uuid)) {
                                    const scheduleDate = resolveAmaronCallScheduledOnDisplayValue(event.additionalColumnValues);
                                    if (scheduleDate !== null && scheduleDate !== undefined) {
                                        return formatDateTime(scheduleDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "loggedOn",
                            displayName: "Logged On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (AmaronEventTypes.Calls as Uuid)) {
                                    const loggedDate = event.additionalColumnValues[AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID];
                                    if (loggedDate !== null && loggedDate !== undefined) {
                                        return formatDateTime(loggedDate as string);
                                    }
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdAtTimestamp",
                            displayName: "Created at",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "journeyOwner",
                            displayName: "Assigned to",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                return resolveUserDisplayName({
                                    userId: resolveAmaronAssignedAgentUserId(event),
                                });
                            },
                        },
                    ];
                }

                if (isEmotoradWorkspace(workspace)) {
                    return [
                        {
                            id: "eventName",
                            displayName: "Event Name",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const eventTemplate = eventTemplates.find((template) => template.id === event.templateId);
                                return eventTemplate !== undefined ? eventTemplate.displayName : "-";
                            },
                        },
                        {
                            id: "eventStatus",
                            displayName: "Status",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                if (event.templateId === (EmotoradEventTypes.call as Uuid)) {
                                    return resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID],
                                        Object.values(emotoradCallStatuses),
                                    );
                                }
                                if (event.templateId === (EmotoradEventTypes.lead as Uuid)) {
                                    const leadStatus = resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID],
                                        Object.values(emotoradLeadEventStatuses),
                                    );
                                    if (leadStatus !== "-") {
                                        return leadStatus;
                                    }
                                    return resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID],
                                        Object.values(emotoradEnquiryDigitalLeadStatuses),
                                    );
                                }
                                if (event.templateId === (EmotoradEventTypes.walkin as Uuid)) {
                                    return resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID],
                                        Object.values(emotoradCallStatuses),
                                    );
                                }
                                if (event.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                    return resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID],
                                        Object.values(emotoradCallStatuses),
                                    );
                                }
                                if (event.templateId === (EmotoradEventTypes.sales as Uuid)) {
                                    return resolveEmotoradStatusDisplayName(
                                        event.additionalColumnValues[EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID],
                                        Object.values(emotoradSalesStatuses),
                                    );
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdBy",
                            displayName: "Created By",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                return resolveUserDisplayName({
                                    userId: event.createdBy,
                                });
                            },
                        },
                        {
                            id: "createdAt",
                            displayName: "Created/Assigned On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "eventOwner",
                            displayName: "Event Owner",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                let ownerId: Uuid | null = null;
                                if (event.templateId === (EmotoradEventTypes.call as Uuid)) {
                                    ownerId = resolveEmotoradCallStyleEventOwnerId(event);
                                } else if (event.templateId === (EmotoradEventTypes.walkin as Uuid)) {
                                    ownerId = resolveEmotoradWalkinStyleEventOwnerId(event);
                                } else if (event.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                    ownerId = resolveEmotoradTestRideStyleEventOwnerId(event);
                                } else if (event.templateId === (EmotoradEventTypes.sales as Uuid)) {
                                    ownerId = event.additionalColumnValues[EMOTORAD__EVENT__SALES__ACTION_OWNER__COLUMN_ID] as Uuid | null;
                                } else if (event.templateId === (EmotoradEventTypes.lead as Uuid)) {
                                    ownerId = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID] as Uuid | null;
                                }
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                        {
                            id: "scheduledOn",
                            displayName: "Scheduled On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                let scheduleDate: unknown = null;
                                if (event.templateId === (EmotoradEventTypes.call as Uuid)) {
                                    scheduleDate = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID];
                                } else if (event.templateId === (EmotoradEventTypes.walkin as Uuid)) {
                                    scheduleDate = event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID];
                                } else if (event.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                    scheduleDate = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID];
                                }
                                if (scheduleDate !== null && scheduleDate !== undefined) {
                                    return formatDateTime(scheduleDate as string);
                                }
                                return "-";
                            },
                        },
                        {
                            id: "loggedOn",
                            displayName: "Logged On",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                let loggedDate: unknown = null;
                                if (event.templateId === (EmotoradEventTypes.call as Uuid)) {
                                    loggedDate = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID];
                                } else if (event.templateId === (EmotoradEventTypes.walkin as Uuid)) {
                                    loggedDate = event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID];
                                } else if (event.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                    loggedDate = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID];
                                } else if (event.templateId === (EmotoradEventTypes.sales as Uuid)) {
                                    loggedDate = event.additionalColumnValues[EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID];
                                }
                                if (loggedDate !== null && loggedDate !== undefined) {
                                    return formatDateTime(loggedDate as string);
                                }
                                return "-";
                            },
                        },
                        {
                            id: "createdAtTimestamp",
                            displayName: "Created at",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => formatDateTime(event.createdAt),
                        },
                        {
                            id: "journeyOwner",
                            displayName: "Assigned to",
                            type: OttopilotColumnType.string,
                            sortingCapability: false,
                            valueGetter: (event: EventWithEventContainer) => {
                                const ownerId = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID] as Uuid | null;
                                if (ownerId === null || ownerId === undefined) {
                                    return "-";
                                }
                                return resolveUserDisplayName({
                                    userId: ownerId,
                                });
                            },
                        },
                    ];
                }

                return [
                    {
                        id: "eventName",
                        displayName: "Event Name",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            const eventType = Object.entries(DuluxEventTypes).find(([_, value]) => value === event.templateId);
                            return eventType !== undefined ? eventType[0] : "Unknown";
                        },
                    },
                    {
                        id: "eventStatus",
                        displayName: "Status",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            if (event.templateId === DuluxEventTypes.Enquiry as Uuid) {
                                const statusId = event.additionalColumnValues[DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
                                if (statusId === null || statusId === undefined) {
                                    return "-";
                                }
                                const status = Object.values(enquiryStatuses).find((s) => s.id === statusId);
                                return status?.displayName ?? "-";
                            }
                            if (event.templateId === DuluxEventTypes.Onboarding as Uuid) {
                                const statusId = event.additionalColumnValues[DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID];
                                if (statusId === null || statusId === undefined) {
                                    return "-";
                                }
                                const status = Object.values(onboardingStatuses).find((s) => s.id === statusId);
                                return status?.displayName ?? "-";
                            }
                            if (event.templateId === DuluxEventTypes.Calls as Uuid) {
                                const statusId = event.additionalColumnValues[DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
                                if (statusId === null || statusId === undefined) {
                                    return "-";
                                }
                                const status = Object.values(callStatuses).find((s) => s.id === statusId);
                                return status?.displayName ?? "-";
                            }
                            return "-";
                        },
                    },
                    {
                        id: "createdBy",
                        displayName: "Created By",
                        type: OttopilotColumnType.string,
                        sortingCapability: true,
                        valueGetter: (event: EventWithEventContainer) => {
                            return resolveUserDisplayName({
                                userId: event.createdBy,
                            });
                        },
                    },
                    {
                        id: "createdAt",
                        displayName: "Created/Assigned On",
                        type: OttopilotColumnType.string,
                        sortingCapability: true,
                        valueGetter: (event: EventWithEventContainer) => {
                            return formatDateTime(event.createdAt);
                        },
                    },
                    {
                        id: "eventOwner",
                        displayName: "Event Owner",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            let ownerId: Uuid | null = null;
                            if (event.templateId === DuluxEventTypes.Enquiry as Uuid) {
                                ownerId = event.additionalColumnValues[DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID] as Uuid | null;
                            } else if (event.templateId === DuluxEventTypes.Onboarding as Uuid) {
                                ownerId = event.additionalColumnValues[DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID] as Uuid | null;
                            } else if (event.templateId === DuluxEventTypes.Calls as Uuid) {
                                ownerId = event.additionalColumnValues[DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID] as Uuid | null;
                            }
                            if (ownerId === null || ownerId === undefined) {
                                return "-";
                            }
                            return resolveUserDisplayName({
                                userId: ownerId,
                            });
                        },
                    },
                    {
                        id: "scheduledOn",
                        displayName: "Scheduled On",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            if (event.templateId === DuluxEventTypes.Calls as Uuid) {
                                const scheduleDate = event.additionalColumnValues[DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID];
                                if (scheduleDate !== null && scheduleDate !== undefined) {
                                    return formatDateTime(scheduleDate as string);
                                }
                            }
                            return "-";
                        },
                    },
                    {
                        id: "loggedOn",
                        displayName: "Logged On",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            if (event.templateId === DuluxEventTypes.Calls as Uuid) {
                                const loggedDate = event.additionalColumnValues[DULUX__EVENT__CALLS__LOGGED_DATE__COLUMN_ID];
                                if (loggedDate !== null && loggedDate !== undefined) {
                                    return formatDateTime(loggedDate as string);
                                }
                            }
                            return "-";
                        },
                    },
                    {
                        id: "createdAtTimestamp",
                        displayName: "Created at",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            const dt = formatDateTime(event.createdAt);
                            return dt;
                        },
                    },
                    {
                        id: "journeyOwner",
                        displayName: "Assigned to",
                        type: OttopilotColumnType.string,
                        sortingCapability: false,
                        valueGetter: (event: EventWithEventContainer) => {
                            const ownerId = event.eventContainer.additionalColumnValues[DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID] as Uuid | null;
                            if (ownerId === null || ownerId === undefined) {
                                return "-";
                            }
                            return resolveUserDisplayName({
                                userId: ownerId,
                            });
                        },
                    },
                ];
            })();

                if (isProfilePresentation !== true) {
                    return baseLineItems;
                }

                if (baseLineItems.some((column) => column.id === "templateId") === true) {
                    return baseLineItems;
                }

                return [...baseLineItems, profileEventTypeFilterColumn];
            })(),
            // [
            //     {
            //         id: "id",
            //         type: OttopilotColumnType.uuid,
            //         displayName: "ID",
            //         valueGetter: (event) => event.id,
            //     },
            //     {
            //         id: "templateId",
            //         type: OttopilotColumnType.uuid,
            //         displayName: "Type",
            //         valueGetter: (event) => event.templateId,
            //         valueFormatter: (value) =>
            //             eventTemplates.find((eventTemplate) => eventTemplate.id === value)?.displayName ?? "?",
            //     },
            //     {
            //         id: "rawData",
            //         type: OttopilotColumnType.string,
            //         displayName: "rawData",
            //         valueGetter: (event) => JSON.stringify(event),
            //     },
            // ],
        },
        views: [
            ...(showProfileListView === true
                ? [
                      createOttopilotListView(
                          JOURNEY_PROFILE_EVENTS_LIST_VIEW_ID,
                          (event: EventWithEventContainer, listCardContext) =>
                              profileEventsListPresentation.renderListCard(event, listCardContext),
                          {
                              onListCardClick: openProfileListCardReadPanel,
                          },
                      ),
                  ]
                : []),
            ...(showTableView === true
                ? [
                      {
                          id: JOURNEY_EVENTS_TABLE_VIEW_ID,
                          viewType: ViewType.table,
                          visibleColumns: [
                              "eventName",
                              "eventStatus",
                              "createdBy",
                              "createdAt",
                              "eventOwner",
                              "scheduledOn",
                              "loggedOn",
                              "createdAtTimestamp",
                              "journeyOwner",
                          ],
                          otherArgs: {},
                      },
                  ]
                : []),
            ...(showKanbanView === true
                ? [
                      {
                          id: JOURNEY_EVENTS_KANBAN_VIEW_ID,
                          viewType: ViewType.kanbanBoard,
                          cardRenderer: (event: EventWithEventContainer) => (
                              <EventKanbanBoardCard
                                  event={event}
                                  dataViewerQueryKeyPrefix={eventsQueryKeyPrefix}
                              />
                          ),
                          columnConfig: eventTemplates.map((eventTemplate, count) => {
                              const color = colorsData[count % colorsData.length] ?? {
                                  background: token("color.background.accent.blue.subtlest"),
                                  textColor: token("color.text.accent.blue"),
                              };
                              return {
                                  id: eventTemplate.id,
                                  displayName: eventTemplate.displayName,
                                  possibleValues: [eventTemplate.id],
                                  background: color.background,
                                  textColor: color.textColor,
                              };
                          }),
                          filterFieldId: "templateId",
                      },
                  ]
                : []),
        ],
        initialViewId:
            profileDefaultViewType === "list"
                ? JOURNEY_PROFILE_EVENTS_LIST_VIEW_ID
                : profileDefaultViewType === "kanban"
                  ? JOURNEY_EVENTS_KANBAN_VIEW_ID
                  : JOURNEY_EVENTS_TABLE_VIEW_ID,
        initialSortConfiguration: {
            revisionId: uniqueUuid`b88695bd-591c-4d1f-a32d-7f25fae5245c`,
            lineItems: [
                {
                    columnId: profileListOverrides?.initialSort?.columnId ?? "createdAt",
                    direction: profileListOverrides?.initialSort?.direction ?? SortDirection.ascending,
                },
            ],
        },
        showDefaultSortAsActive: true,
        ...(isProfilePresentation
            ? {
                  searchColumns: ["eventName"],
                  searchPlaceholder: "Search",
                  defaultCreatedAtMonthFilter: profileListOverrides?.defaultCreatedAtMonthFilter ?? true,
                  scopeFilterConfig: profileScopeFilterConfig,
                  initialVisibleFilters: {
                      revisionId: uniqueUuid`c5d4e3f2-6a7b-8c9d-0e1f-2a3b4c5d6e7f`,
                      lineItems: ["templateId"],
                  },
                  canEditRow: (row: EventWithEventContainer) => {
                      const editEnabledEventTemplateIds = new Set(
                          profileListOverrides?.editEnabledEventTemplateIds ?? [],
                      );
                      if (editEnabledEventTemplateIds.size > 0) {
                          if (editEnabledEventTemplateIds.has(row.templateId) !== true) {
                              return false;
                          }
                      } else {
                          const editDisabledEventTemplateIds = new Set(
                              profileListOverrides?.editDisabledEventTemplateIds ?? [],
                          );
                          if (editDisabledEventTemplateIds.has(row.templateId) === true) {
                              return false;
                          }
                      }
                      if (profileListOverrides?.isRowActionDisabled?.("edit", row) === true) {
                          return false;
                      }
                      return true;
                  },
                  readPanelComponent: (row: EventWithEventContainer, isExpanded: boolean) => {
                      const eventTemplate = eventTemplates.find((template) => template.id === row.templateId);
                      if (eventTemplate === undefined) {
                          return null;
                      }
                      return (
                          <EventViewPanel
                              row={row}
                              isExpanded={isExpanded}
                              eventTemplate={eventTemplate}
                              journeysModuleConfiguration={eventContainerTemplate}
                              eventTemplates={eventTemplates}
                              notesEventContainerSourceLabel={notesEventContainerSourceLabel}
                          />
                      );
                  },
                  updatePanelComponent: (row: EventWithEventContainer, isExpanded: boolean) => {
                      const eventTemplate = eventTemplates.find((template) => template.id === row.templateId);
                      if (eventTemplate === undefined) {
                          return null;
                      }
                      return (
                          <EventEditPanel
                              row={row}
                              isExpanded={isExpanded}
                              eventTemplate={eventTemplate}
                              journeysModuleConfiguration={eventContainerTemplate}
                              eventTemplates={eventTemplates}
                              notesEventContainerSourceLabel={notesEventContainerSourceLabel}
                              queryKeyPrefix={eventsQueryKeyPrefix}
                              onSuccess={(updatedData) => {
                                  eventsDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel((previous) => {
                                      if (previous.rowEngagementType === RowEngagementType.none) {
                                          return previous;
                                      }
                                      return {
                                          rowEngagementType: RowEngagementType.read,
                                          rowEngagementPanelType: previous.rowEngagementPanelType,
                                          rowEngagementData: updatedData as EventWithEventContainer,
                                      };
                                  });
                              }}
                          />
                      );
                  },
                  tableRowActions: profileTableRowActions,
              }
            : {}),
    });

    eventsDataViewerControllerRef.current = eventsDataViewerController;

    useEffect(() => {
        if (isProfilePresentation !== true || registerDismissEventRowEngagement === undefined) {
            return;
        }
        registerDismissEventRowEngagement(() => {
            eventsDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel({
                rowEngagementType: RowEngagementType.none,
                rowEngagementPanelType: RowEngagementPanelType.none,
                rowEngagementData: null,
            });
            closeNotesPanel();
        });
    }, [closeNotesPanel, isProfilePresentation, registerDismissEventRowEngagement]);

    const viewModeControl = useDataViewerViewModeControl(eventsDataViewerController);

    return (
        <div
            className={twJoin(
                isProfilePresentation ? "w-full h-full min-h-0" : "w-full h-[100svh] sm:-mx-ds-space-300",
                className,
            )}
            style={style}
        >
            {isProfilePresentation ? (
                <>
                    {eventRowEngagement.archiveModals}
                    {eventRowEngagement.scheduleCallModal}
                    {listNotesPanelChrome}
                </>
            ) : null}
            {/* <OttopilotTable
                queryKeyPrefix={["08dfbbd5-592c-4c3d-a900-05733d03e61a"]}
                rowsQueryFn={async () => events}
                rowCountQueryFn={async () => events.length}
                columns={eventColumns}
                otherArgs={{
                    rowClass: "cursor-pointer",
                    onRowClicked: (event) => {
                        navigate(`/w/${workspace.id}/ottopilot/events/${event.data.id}`);
                    },
                }}
            /> */}

            {/* <div className="grid grid-flow-col auto-cols-auto gap-x-ds-space-100 justify-start items-center">
                <ItemBuilder
                    items={eventTemplates}
                    itemBuilder={(eventTemplate) => (
                        <button
                            type="button"
                            className={twJoin(
                                "px-2 py-1 rounded-[0.25rem]",
                                "border-ds-border-width border-solid border-ds-border",
                                eventTemplate.id === selectedEventTemplateId
                                    ? "bg-ds-background-selected text-ds-text-selected text-ds-font-body !font-ds-font-weight-medium"
                                    : null,
                            )}
                            onClick={() => {
                                setSelectedEventTemplateId(eventTemplate.id);
                                setPaginationDetails((prev) => {
                                    const newData = prev;
                                    newData.offset = 0;
                                    return newData;
                                });
                            }}
                            key={eventTemplate.id}
                        >
                            {eventTemplate.displayName}
                        </button>
                    )}
                />
            </div> */}

            {/* <VerticalSpacer className="h-ds-space-200 sm:h-ds-space-300" /> */}

            <OttopilotDataViewer
                controller={eventsDataViewerController}
                hideAddFilterButton
                hideViewPicker={isProfilePresentation}
                hidePageFooter={isProfilePresentation}
                hideConfigureColumns={isProfilePresentation}
                hideMobileOverflowMenu={isProfilePresentation}
                horizontalInsetClass={
                    isProfilePresentation ? ENTITY_PROFILE_HORIZONTAL_INSET_CLASS : undefined
                }
                stickyToolbar={isProfilePresentation}
                filterLeadingContent={isProfilePresentation ? profileFilterLeadingContent : undefined}
                toolbarTrailingContent={
                    isProfilePresentation ? (
                        <ViewModeSegmentedControl
                            selectedView={viewModeControl.selectedViewMode}
                            onViewChange={viewModeControl.onViewModeChange}
                            views={viewModeControl.availableViewModes}
                        />
                    ) : undefined
                }
            />
        </div>
    );
}
